import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { ENDPOINTS as GENERAL_ENDPOINTS } from '../../../../General/Utils/Endpoints';
import { colorNormalize } from '../../../Objetos/ColoresModel';
import { normalizeValue } from '../../../../../utils/Texts';
import { getSessionUserId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from './ColoresModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import '../../../Styles/ColoresCatalog.css'; // Reutilizamos estilos estándar
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 12;
const endpoint = ENDPOINTS.colores;
const endpointById = ENDPOINTS.colorById;
const modelo = colorNormalize;

export default function ColoresCatalog() {
    const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Colores');
    const esSuperAdmin = isSessionSuperAdmin();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
    const [idEmpresa, setIdEmpresa] = useState(null);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setModalOpen(false);
        setDeleteOpen(false);
        setAlertConfig({ open: true, message, variant, title });
    };

    const editorRef = useRef(null);

    // Scroll to editor when opened
    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [modalOpen, selectedId]);

    const loadData = async (cid = idEmpresa) => {
        setLoading(true);
        try {
            let url = endpoint;
            if (cid) url += `?idEmpresa=${cid}`;
            const res = await axios.get(url);
            const data = (res.data || []).map((x) => modelo(x));
            setRows(data);
        } catch (error) {
            console.error('Error al cargar colores:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            let foundId = null;
            const uid = getSessionUserId();
            if (uid) {
                try {
                    const res = await axios.get(GENERAL_ENDPOINTS.empresasUsuario(uid));
                    if (res.data && res.data.length > 0) {
                        foundId = res.data[0].idEmpresa || res.data[0].IdEmpresa;
                        setIdEmpresa(foundId);
                    }
                } catch (e) { console.error('Error fetching company:', e); }
            }
            loadData(foundId);
        };
        init();
    }, []);

    const filtered = useMemo(() => {
        let data = rows;
        if (query.trim()) {
            const q = normalizeValue(query);
            data = data.filter((e) => {
                const nom = normalizeValue(e.nombre);
                const vari = normalizeValue(e.variable);
                const val = normalizeValue(e.valor);
                const emp = normalizeValue(e.nombreEmpresa || 'Global (Default)');
                return nom.includes(q) || vari.includes(q) || val.includes(q) || emp.includes(q);
            });
        }
        return data;
    }, [rows, query]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const handleNew = () => {
        setSelectedId(null);
        setEditData(null);
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (color) => {
        setSelectedId(color.idColor);
        setEditData(color);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (id, name) => {
        setDeleteTarget({ id, name });
        setDeleteOpen(true);
    };

    const doDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(endpointById(deleteTarget.id));
            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            triggerAlert('Error al eliminar');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (form, mode) => {
        try {
            setLoading(true);
            const payload = {
                IdColor: mode === 'new' ? 0 : selectedId,
                Nombre: form.nombre,
                Descripcion: form.descripcion,
                IdCatColor: form.idCatColor,
                Valor: form.valor,
                Activo: form.activo ?? true,
                IdCategoria: form.idCategoria,
                IdEmpresa: idEmpresa
            };

            if (mode === 'new') {
                await axios.post(endpoint, payload);
            } else {
                await axios.put(endpoint, payload);
            }
            setModalOpen(false);
            await loadData();

            try {
                const { loadAndApplyActiveTheme } = await import('../../../Utils/ThemeManager');
                loadAndApplyActiveTheme();
            } catch (ignore) { }
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">Catálogo de Colores</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    {puedeCrear && (
                        <button className="btn-action" onClick={handleNew}>
                            ＋ Nuevo
                        </button>
                    )}
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    {esSuperAdmin && <th>Empresa</th>}
                                    <th>Variable CSS</th>
                                    <th>Valor</th>
                                    <th style={{ textAlign: 'center' }}>Activo</th>
                                    {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                                    {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan={(esSuperAdmin ? 5 : 4) + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)} style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
                                            No se encontraron registros.
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((row) => (
                                        <tr key={row.idColor}>
                                            <td style={{ fontWeight: 600 }}>{row.nombre}</td>
                                            {esSuperAdmin && <td style={{ color: '#0f172a', fontWeight: 500 }}>{row.nombreEmpresa || 'Global (Default)'}</td>}
                                            <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{row.variable}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{ width: '18px', height: '18px', background: row.valor, border: '1px solid #cbd5e1', borderRadius: '4px' }}></div>
                                                    <span style={{ fontFamily: 'monospace' }}>{row.valor}</span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {row.activo ? 'Sí' : 'No'}
                                            </td>
                                            {puedeEditar && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(row)} title="Editar">
                                                        <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                                                    </button>
                                                </td>
                                            )}
                                            {puedeEliminar && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button className="rk-iconbtn rk-iconbtn--del" onClick={() => askDelete(row.idColor, row.nombre)} title="Borrar">
                                                        <img src={sigmaDelete} alt="Borrar" className="rk-ico" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="roles-pagination">
                        <div className="roles-pagination-left">
                            <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                        </div>
                        <div className="roles-pagination-right">
                            <button
                                className={`roles-page-btn ${page === 1 ? 'is-disabled' : ''}`}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >‹</button>
                            {pageNumbers.map(n => (
                                <button
                                    key={n}
                                    className={`roles-page-btn ${n === page ? 'is-active' : ''}`}
                                    onClick={() => setPage(n)}
                                >{n}</button>
                            ))}
                            <button
                                className={`roles-page-btn ${page === totalPages ? 'is-disabled' : ''}`}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >›</button>
                        </div>
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className="roles-editor-slot" ref={editorRef} style={{ marginTop: '20px' }}>
                    <Modal
                        open={modalOpen}
                        mode={modalMode}
                        data={editData}
                        onClose={() => setModalOpen(false)}
                        onSubmit={handleSubmit}
                        usedIds={rows.map(r => r.idCatColor)}
                        inline={true}
                    />
                </div>
            )}

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
